import React, { useRef, useState, forwardRef } from 'react'
import useSWR from 'swr'
import { authRequest } from './api'
import { useOnClickOutside } from './hooks'
import SecureButton from './SecureButton'

function TextWithTemplate({
  kind = '',
  textContent = '',
  setTextContent,
  textAreaRef,
  textareaLabel,
  selectLabel,
  selectProps,
  ...props
}) {
  const saveTemplateRef = useRef(null)
  const templateNameRef = useRef(null)
  const kindRef = useRef(null)

  const { data: templates, mutate } = useSWR(
    `/admin/templates?kind=${kind}`,
    authRequest
  )

  const isntKindSpecified = !kind || kind === ''

  const [isSavingTemplate, setSavingTemplate] = useState(false)
  const [templateName, setTemplateName] = useState('')
  const [templateSelected, setTemplateSelected] = useState('')

  const textMatchTemplate =
    templateSelected !== '' &&
    templates.find(({ name }) => templateSelected === name) &&
    templates.find(({ name }) => templateSelected === name).content !==
      textContent

  useOnClickOutside(saveTemplateRef, () => setSavingTemplate(false))

  async function saveTemplate() {
    if (templateName !== '' && textContent !== '') {
      await authRequest('/admin/template', {
        method: 'POST',
        body: JSON.stringify({
          name: templateName,
          content: textContent,
          kind: isntKindSpecified ? kindRef.current.value : kind,
        }),
      })
      setSavingTemplate(false)
      await mutate()
      setTemplateSelected(templateName)
      setTemplateName('')
    }
  }

  async function replaceTemplate() {
    if (templateSelected !== '') {
      const id = templates.find(({ name }) => templateSelected === name)._id
      await authRequest('/admin/template', {
        method: 'PATCH',
        body: JSON.stringify({
          content: textContent,
          id,
        }),
      })
      await mutate()
    }
  }

  return (
    <>
      {selectLabel ? <label htmlFor="select">{selectLabel}</label> : null}

      {templateSelected !== '' && !textMatchTemplate && (
        <SecureButton
          confirm={`Sure Delete "${templateSelected}"`}
          className="new-btn"
          confirmClassName="new-btn"
          type="button"
          onConfirm={async () => {
            await authRequest('/admin/template', {
              method: 'DELETE',
              body: JSON.stringify({
                id: templates.find(({ name }) => templateSelected === name)._id,
              }),
            })
            await mutate()
            setTemplateSelected('')
            setTextContent('')
          }}
        >
          Delete Template
        </SecureButton>
      )}

      <select
        id="select"
        {...selectProps}
        onChange={(e) => {
          const { value } = e.target
          setTemplateSelected(value)
          if (
            value !== '' &&
            templates.find(({ name }) => name === e.target.value)
          ) {
            setTextContent(
              templates.find(({ name }) => name === e.target.value).content
            )
          }
        }}
        value={templateSelected}
        style={{ marginBottom: '.5em' }}
      >
        <option value="">Select a Template…</option>
        {templates &&
          templates.map(({ _id, name, kind: _kind }) => (
            <option value={name} key={_id}>
              {name} {isntKindSpecified ? `[${_kind}]` : ''}
            </option>
          ))}
      </select>

      {textareaLabel ? <label htmlFor="textarea">{textareaLabel}</label> : null}

      {textContent !== '' && (templateSelected === '' || textMatchTemplate) && (
        <span ref={saveTemplateRef}>
          {isSavingTemplate && (
            <>
              <input
                type="text"
                className="small-input"
                ref={templateNameRef}
                value={templateName}
                onChange={(e) => {
                  e.preventDefault()
                  setTemplateName(e.target.value)
                }}
                placeholder="Template Name"
                onKeyDown={(e) => {
                  if (e.keyCode === 13 && !e.shiftKey) {
                    e.preventDefault()
                    saveTemplate()
                  }
                }}
              />
              {isntKindSpecified ? (
                <select
                  ref={kindRef}
                  style={{
                    marginLeft: '0.6em',
                    verticalAlign: 'middle',
                  }}
                >
                  <option value="message">Message</option>
                  <option value="chat">Chat</option>
                </select>
              ) : null}
            </>
          )}
          <button
            type="button"
            className="new-btn"
            style={{ marginLeft: '1em', position: 'relative' }}
            onClick={async () => {
              if (isSavingTemplate) {
                saveTemplate()
              } else {
                setSavingTemplate(true)
                setTimeout(() => templateNameRef.current.focus(), 250)
              }
            }}
            disabled={isSavingTemplate && templateName === ''}
          >
            {isSavingTemplate ? 'Confirm' : 'Save as Template'}
          </button>
          {templateSelected !== '' && (
            <SecureButton
              confirm={`Sure Replace "${templateSelected}"`}
              className="new-btn"
              confirmClassName="new-btn"
              type="button"
              onConfirm={async () => {
                replaceTemplate()
              }}
            >
              Replace Template {templateSelected}
            </SecureButton>
          )}
        </span>
      )}

      <textarea
        id="textarea"
        onChange={(e) => setTextContent(e.target.value)}
        value={textContent}
        ref={textAreaRef}
        {...props}
      />
    </>
  )
}

export default forwardRef((props, ref) => (
  <TextWithTemplate {...props} textAreaRef={ref} />
))
